import { colors, uniqueNamesGenerator } from "unique-names-generator";

import { LayoutLocator, SortablePayload } from "./types";

export const getInitials = (name: string) => {
  const regex = new RegExp(/(\p{L}{1})\p{L}+/, "gu");
  const initials = [...name.matchAll(regex)] || [];

  return ((initials.shift()?.[1] || "") + (initials.pop()?.[1] || "")).toUpperCase();
};

export const isUrl = (string: string | null | undefined) => {
  if (!string) return false;

  const urlRegex = /https?:\/\/[^ \n]+/i;

  return urlRegex.test(string);
};

export const isEmptyString = (string: string) => {
  if (string === "<p></p>") return true;
  return string.trim().length === 0;
};

export const extractUrl = (string: string) => {
  const urlRegex = /https?:\/\/[^ \n]+/i;

  const result = string.match(urlRegex);
  return result ? result[0] : null;
};

export const kebabCase = (string?: string | null) => {
  if (!string) return "";

  return (
    string
      .match(/[A-Z]{2,}(?=[A-Z][a-z]+[0-9]*|\b)|[A-Z]?[a-z]+[0-9]*|[A-Z]|[0-9]+/g)
      ?.join("-")
      .toLowerCase() ?? ""
  );
};

/**
 * A resume's name from the file it was uploaded as: "Michael_Buggy_CV.pdf" becomes
 * "Michael Buggy CV". Returns undefined when nothing usable is left (the caller then picks a name).
 */
export const titleFromFileName = (fileName?: string | null) => {
  if (!fileName) return undefined;

  const title = fileName
    .replace(/^.*[\\/]/, "")
    .replace(/\.[^.]*$/, "")
    .replace(/_+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 100)
    .trim();

  return title || undefined;
};

export const generateRandomName = () => {
  const Qualities = [
    "Visionary",
    "Expert",
    "Master",
    "Specialist",
    "Guru",
    "Leader",
    "Pioneer",
    "Innovator",
    "Strategist",
    "Architect",
  ];
  const Power = [
    "Dynamic",
    "Agile",
    "Efficient",
    "Proactive",
    "Skilled",
    "Talented",
    "Experienced",
    "Versatile",
    "Proficient",
    "Dedicated",
  ];
  const Industry = [
    "Developer",
    "Engineer",
    "Manager",
    "Consultant",
    "Analyst",
    "Designer",
    "Coordinator",
    "Director",
    "Technician",
    "Operator",
    "Architect",
  ];
  return uniqueNamesGenerator({
    // dictionaries: [adjectives, adjectives, animals],
    dictionaries: [Power, Qualities, Industry],
    style: "capital",
    separator: " ",
    length: 3,
  });
};

export const generateRandomCustomSectionName = () => {
  return uniqueNamesGenerator({
    dictionaries: [["Custom Section"], colors],
    separator: " - ",
    length: 2,
  });
};

export const processUsername = (string?: string | null) => {
  if (!string) return "";

  return string.replace(/[^a-zA-Z0-9-.]/g, "").toLowerCase();
};

/**
 * Canonical email normalization. Clerk stores/returns emails lowercased, so we lowercase and trim
 * on our side too — otherwise a stored `Jane@Example.com` would never match Clerk's `jane@…` and
 * the account link would silently miss (creating a duplicate, orphaning the user's data).
 * Idempotent and safe to call on already-normalized values.
 */
export const normalizeEmail = (email?: string | null) => {
  if (!email) return "";

  return email.trim().toLowerCase();
};

export const parseLayoutLocator = (payload: SortablePayload | null): LayoutLocator => {
  if (!payload) return { page: 0, column: 0, section: 0 };

  const section = payload.index as number;
  const [page, column] = payload.containerId.split(".").map(Number);

  return { page, column, section };
};
